import {
  parse,
  Kind,
  DocumentNode,
  OperationDefinitionNode,
  FragmentDefinitionNode,
  SelectionSetNode,
  VariableDefinitionNode,
} from "graphql";

/**
 * Merges a wrapper query string (with a placeholder) and an injectable query document (with fragments and a root query)
 * into a single valid DocumentNode suitable for Apollo.
 *
 * @param wrapperQueryStr - the GraphQL query wrapper with a placeholder fragment spread (e.g. "...<fragmentName>")
 * @param injectedQueryStr - the query string containing `query x(...) { ... }` and fragments
 * @param fragmentName - the dynamically generated fragment name to inject into the wrapper
 * @param fragmentType - the type of the injected fragment (e.g. "Entity" or "Location")
 */
export function mergeQueryWithWrapper(
  wrapperQueryStr: string,
  injectedQueryStr: string,
  fragmentName: string,
  fragmentType: string
): DocumentNode {
  const wrapperDoc = parse(wrapperQueryStr);
  const injectedDoc = parse(injectedQueryStr);

  const wrapperOp = wrapperDoc.definitions.find(
    def => def.kind === Kind.OPERATION_DEFINITION
  ) as OperationDefinitionNode;

  const injectedOp = injectedDoc.definitions.find(
    def => def.kind === Kind.OPERATION_DEFINITION
  ) as OperationDefinitionNode;

  if (!wrapperOp || !injectedOp) {
    throw new Error("Both wrapper and injected queries must contain one operation definition each.");
  }

  // Merge variable definitions
  const wrapperVars = wrapperOp.variableDefinitions ?? [];
  const injectedVars = injectedOp.variableDefinitions ?? [];
  const wrapperVarNames = new Set(wrapperVars.map(v => v.variable.name.value));
  const mergedVarDefs: VariableDefinitionNode[] = [
    ...wrapperVars,
    ...injectedVars.filter(v => !wrapperVarNames.has(v.variable.name.value)),
  ];

  // Extract the selection set of the injected operation
  const injectedSelectionSet = injectedOp.selectionSet;

  // if (injectedSelectionSet.selections.length !== 1) {
  //   throw new Error("Injected query must have a single root field to inject.");
  // }

  // const innerRootField = injectedSelectionSet.selections[0];
  // if (innerRootField.kind !== Kind.FIELD) {
  //   throw new Error("Injected query must start with a field (not a fragment spread or inline fragment).");
  // }

  // Create a wrapper fragment around the injected root field selection set
  const wrapperFragment: FragmentDefinitionNode = {
    kind: Kind.FRAGMENT_DEFINITION,
    name: { kind: Kind.NAME, value: fragmentName },
    typeCondition: { kind: Kind.NAMED_TYPE, name: { kind: Kind.NAME, value: fragmentType } },
    selectionSet: injectedOp.selectionSet as SelectionSetNode,
  };

  // Collect all top-level fragments from the injected query
  const injectedFragments = injectedDoc.definitions.filter(
    (def): def is FragmentDefinitionNode => def.kind === Kind.FRAGMENT_DEFINITION
  );

  // Create a final operation definition with updated variables
  const finalOperation: OperationDefinitionNode = {
    ...wrapperOp,
    variableDefinitions: mergedVarDefs,
  };

  // Create final DocumentNode with:
  // - Merged operation
  // - The dynamically generated wrapper fragment
  // - All original fragments from the injected document
  return {
    kind: Kind.DOCUMENT,
    definitions: [
      finalOperation,
      wrapperFragment,
      ...injectedFragments,
    ],
  };
}
